import { FC, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { of } from 'rxjs';
import { RxUtils, Tr } from '@utils';
import { useService } from '@hooks';
import { ParkAreaAssesmentAnswerService, ParkAreaAssesmentService } from '@services';
import { Stack } from '@mui/material';
import { FormStepLayoutProgress, PortalPageTitle } from '@components/portal/common';
import { IParkAreaAssementQuestionProps, ParkAreaAssementQuestion } from '@components/portal/parking-area';
import { PortalRoutePath } from '@models/router';

export const EditAssesment: FC = () => {
	const navigate = useNavigate();
	const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
	const { parkAreaId = '', assesmentId = '' } = useParams<{ parkAreaId: string; assesmentId: string }>();
	const { data: parkAreaAssesment, error } = useService(
		() => (parkAreaId && assesmentId ? new ParkAreaAssesmentService(parkAreaId).getByAssesmentId(assesmentId) : of()),
		[parkAreaId, assesmentId, activeQuestionIndex]
	);

	const setActiveQuestionIndexRanged = (index: number) => {
		const questionsLength = parkAreaAssesment?.assesment?.questions?.length || 0;
		setActiveQuestionIndex(questionsLength ? Math.max(0, Math.min(questionsLength - 1, index)) : 0);
	};

	const onBack = () => setActiveQuestionIndexRanged(activeQuestionIndex - 1);
	const onNext: IParkAreaAssementQuestionProps['onNext'] = (question, userAnswer, originalAnswer) => {
		const answerService = new ParkAreaAssesmentAnswerService(parkAreaAssesment?.id || '');
		RxUtils.promisify(
			originalAnswer
				? answerService.update(originalAnswer.id, { answer: userAnswer })
				: answerService.create({ assesmentQuestionId: question.id, answer: userAnswer }),
			() => {
				const questionsLength = parkAreaAssesment?.assesment?.questions?.length || 0;
				if (activeQuestionIndex + 1 >= questionsLength) {
					navigate(`/portal/${PortalRoutePath.LOCATION}`);
				} else {
					setActiveQuestionIndexRanged(activeQuestionIndex + 1);
				}
			}
		);
	};

	if (error) {
		return <Navigate to=".." />;
	}

	return (
		<Stack direction="row" sx={{ height: 1.0, maxHeight: 1.0 }}>
			<Stack direction="column" spacing={10} sx={{ height: 1.0, maxHeight: 1.0, flexGrow: 1, paddingY: 5 }}>
				<PortalPageTitle
					paddingX={3}
					title={<Tr.Portal path="parking-area-assesment.title" />}
					subtitle={
						<Tr.Portal path="parking-area-assesment.subtitle" options={{ name: parkAreaAssesment?.assesment.name || '' }} />
					}
					onBackButtonClick={() => navigate(-1)}
				/>
				{parkAreaAssesment?.assesment?.questions ? (
					<ParkAreaAssementQuestion
						questions={parkAreaAssesment.assesment.questions}
						answers={parkAreaAssesment.parkAreaAssesmentAnswers}
						index={activeQuestionIndex}
						onBack={onBack}
						onNext={onNext}
					/>
				) : null}
			</Stack>

			<FormStepLayoutProgress
				step={activeQuestionIndex}
				sx={{ marginY: 6, marginRight: 4 }}
				steps={(parkAreaAssesment?.assesment?.questions || [])?.map((_, i) => ({
					title: <Tr.Portal path="parking-area-assesment.question-progress-title" options={{ index: i + 1 }} />
				}))}
			/>
		</Stack>
	);
};
